// The header's Features and Compare menus.
//
// Both panels ship open and unhidden, so the feature and comparison pages are
// reachable (and crawlable) with JavaScript off. This upgrades them to real
// dropdowns: closed by default, opened by hover or by their button, dismissed
// on outside click or Escape. Opening one closes the other.

// How long the pointer may sit outside a menu before it closes. Enough to cross
// the gap between the button and its panel on a diagonal without losing it.
const CLOSE_DELAY = 180

export function setupNavMenus() {
  const menus = [...document.querySelectorAll("[data-nav-menu]")]

  // Hover-to-open is for pointers that can hover without committing. On touch,
  // the first tap would both open the menu and count as a hover, so those
  // devices keep the button as the only way in.
  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)")

  const controllers = menus.map((menu) => {
    const toggle = menu.querySelector(".nav-menu__toggle")
    const panel = menu.querySelector(".nav-menu__panel")
    if (!toggle || !panel) return null

    const setOpen = (open) => {
      toggle.setAttribute("aria-expanded", String(open))
      panel.hidden = !open
    }

    menu.classList.add("is-enhanced")
    setOpen(false)

    return { menu, toggle, setOpen, isOpen: () => toggle.getAttribute("aria-expanded") === "true" }
  }).filter(Boolean)

  const openOnly = (current, open) =>
    controllers.forEach((other) => other.setOpen(other === current && open))

  controllers.forEach((current) => {
    // Reaching the button with a mouse opens the menu before the click lands,
    // so a plain toggle would read that click as "close" and the button would
    // look broken. The first click after a hover-open just takes ownership of
    // the state; the next one closes, the way the button always has.
    let openedByHover = false

    current.toggle.addEventListener("click", (event) => {
      event.stopPropagation()
      if (current.isOpen() && openedByHover) {
        openedByHover = false
        return
      }
      openOnly(current, !current.isOpen())
    })

    // The panel hangs 14px below the button, outside .nav-menu's own box, so
    // the pointer leaves the menu on the way down to it. chrome.css bridges
    // that gap; the delay covers the rest, and re-entering cancels the close.
    let closing
    const cancelClose = () => { clearTimeout(closing); closing = undefined }

    current.menu.addEventListener("pointerenter", (event) => {
      if (event.pointerType !== "mouse" || !canHover.matches) return
      cancelClose()
      openedByHover = !current.isOpen()
      openOnly(current, true)
    })

    current.menu.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "mouse" || !canHover.matches) return
      cancelClose()
      // Keep a menu the keyboard is inside open, whatever the pointer does.
      closing = setTimeout(() => {
        if (current.menu.contains(document.activeElement)) return
        current.setOpen(false)
        openedByHover = false
      }, CLOSE_DELAY)
    })
  })

  document.addEventListener("click", (event) => {
    controllers.forEach((c) => {
      if (!c.menu.contains(event.target)) c.setOpen(false)
    })
  })

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return
    const open = controllers.find((c) => c.isOpen())
    if (!open) return
    open.setOpen(false)
    open.toggle.focus()
  })
}
